import { useCallback, useEffect, useRef, useState } from "react";

import { type ApiError, isAbort, toApiError } from "../api/client";

interface Resolved<T> {
  key: string | null;
  data?: T;
  error?: ApiError;
}

export interface AsyncState<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  reload: () => void;
  update: (change: (current: T | undefined) => T | undefined) => void;
}

/**
 * Load data whenever `key` changes. Previous data stays visible while the next
 * request is in flight (no flicker when paging or filtering); superseded requests
 * are aborted.
 */
export function useAsync<T>(load: (signal: AbortSignal) => Promise<T>, key: string): AsyncState<T> {
  const [resolved, setResolved] = useState<Resolved<T>>({ key: null });
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  const requestKey = `${key}#${nonce}`;

  useEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();
    loadRef.current(controller.signal).then(
      (data) => setResolved({ key: requestKey, data }),
      (error: unknown) => {
        if (controller.signal.aborted || isAbort(error)) return;
        setResolved((previous) => ({ key: requestKey, data: previous.data, error: toApiError(error) }));
      },
    );
    return () => controller.abort();
  }, [requestKey]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const update = useCallback(
    (change: (current: T | undefined) => T | undefined) =>
      setResolved((previous) => ({ ...previous, data: change(previous.data) })),
    [],
  );

  const loading = resolved.key !== requestKey;
  return { data: resolved.data, error: loading ? undefined : resolved.error, loading, reload, update };
}
